import { useCallback, useEffect, useMemo, useState } from 'react';
import * as Checkbox from '@radix-ui/react-checkbox';

import {
  AgreementDTO,
  DirectoryDTO,
  InsuranceObjectDTO,
} from '@/types/api-types';

import IconClose from '@/assets/svg/icon-close.svg?react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useGlobalStore } from '@/stores/globalStore';

import {
  INDEX_OBJECT_TYPES,
  INSURANCE_AREA,
} from '@/constants/global-constants';

interface SecondStepFileAddFilesProps {
  handleUploadFiles: (agreementUuid: string, prePath: string) => void;
  currentAgreementUuid: string;
  filesToUploadLength: number;
  isUploading: boolean;
  hasInvalidFiles: boolean;
  hasOnlyDateErrors: boolean;
  ignoreDateErrors: boolean;
  setIgnoreDateErrors: (value: boolean) => void;
}

const SecondStepFileAddFiles = ({
  handleUploadFiles,
  currentAgreementUuid,
  filesToUploadLength,
  isUploading,
  hasInvalidFiles,
  hasOnlyDateErrors,
  ignoreDateErrors,
  setIgnoreDateErrors,
}: SecondStepFileAddFilesProps) => {
  const {
    directories,
    insuranceObjects,
    agreements,
    getInsuranceObjects,
    getAgreements,
  } = useApiManagerStore();
  const { getIndexById } = useGlobalStore();

  const [currentAgreement, setCurrentAgreement] = useState<AgreementDTO | null>(
    null,
  );
  const [currentDirectory, setCurrentDirectory] = useState<DirectoryDTO | null>(
    null,
  );
  const [currentInsuranceObject, setCurrentInsuranceObject] =
    useState<InsuranceObjectDTO | null>(null);

  const getAgreement = async (uuid: string) => {
    const agreements = await getAgreements([uuid]);
    return agreements[0];
  };

  const setCurrentAgreementCallback = useCallback(
    async (agreementUuid: string) => {
      const agreement = agreements.find(
        (agreement) => agreement.uuid === agreementUuid,
      );
      if (agreement) {
        setCurrentAgreement(agreement);
      } else {
        const agreement = await getAgreement(agreementUuid);
        setCurrentAgreement(agreement);
      }
    },
    [agreements],
  );

  useEffect(() => {
    if (currentAgreementUuid) {
      setCurrentAgreementCallback(currentAgreementUuid);
    }
  }, [currentAgreementUuid]);

  const currentIndex = useMemo(() => {
    if (currentAgreement) {
      const index = getIndexById(currentAgreement.index);
      return index;
    }
    return null;
  }, [currentAgreement, getIndexById]);

  // Checkbox should be visible only for "Страхування ТЗ"
  const showIgnoreDateErrorsCheckbox = useMemo(() => {
    return (
      currentIndex?.insurance_area === INSURANCE_AREA.INSURANCE &&
      currentInsuranceObject?.insurance_object_type ===
        INDEX_OBJECT_TYPES.VEHICLE
    );
  }, [
    currentIndex?.insurance_area,
    currentInsuranceObject?.insurance_object_type,
  ]);

  useEffect(() => {
    const getInsuranceObject = async (uuid: string) => {
      const insuranceObjects = await getInsuranceObjects([uuid]);
      if (insuranceObjects.length > 0) {
        setCurrentInsuranceObject(insuranceObjects[0]);
      }
    };
    if (currentAgreement) {
      const insuranceObject = insuranceObjects.find(
        (insuranceObject) =>
          insuranceObject.uuid === currentAgreement.insurance_object,
      );
      if (insuranceObject) {
        setCurrentInsuranceObject(insuranceObject);
      } else {
        getInsuranceObject(currentAgreement.insurance_object);
      }
    }
  }, [currentAgreement, insuranceObjects]);

  useEffect(() => {
    if (directories.length > 0 && currentInsuranceObject) {
      const directory = directories.find(
        (directory) => directory.uuid === currentInsuranceObject.directory,
      );
      if (directory) {
        setCurrentDirectory(directory);
      }
    }
  }, [directories, currentInsuranceObject]);

  const handleSubmit = () => {
    handleUploadFiles(
      currentAgreementUuid,
      `${currentDirectory?.uuid}/${currentInsuranceObject?.uuid}`,
    );
  };

  return (
    <div className="flex h-full min-w-[300px] flex-col justify-between overflow-y-auto rounded-md bg-white p-6 shadow-sm sm:w-1/3">
      <div className="flex flex-col">
        <p className="mb-2.5 text-2xl font-medium">
          Крок №2: Інформація про файли
        </p>
        <p className="mb-5 text-base font-medium">
          Після завантаження файлу(ів) необхідно додати інформацію про об'єкт
          страхування до якого він (вони) відносяться
        </p>
        <div className="mb-6 w-full">
          <Input value={currentDirectory?.name} readOnly />
        </div>
        <div>
          <div className="mb-6 flex items-center justify-between">
            <span className="text-base font-medium">
              Об'єкт страхування
              <br />
              або врегулювання
            </span>
            <div
              className={cn(
                'grid h-[34px] w-[170px] grid-cols-2 rounded-md border border-gray-200',
              )}
            >
              <div
                className={cn(
                  'flex items-center justify-center rounded-md font-medium text-gray-200',
                  currentInsuranceObject?.insurance_object_type ===
                    INDEX_OBJECT_TYPES.VEHICLE &&
                    'border border-primary text-primary',
                )}
              >
                ТЗ
              </div>
              <div
                className={cn(
                  'flex items-center justify-center rounded-md font-medium text-gray-200',
                  currentInsuranceObject?.insurance_object_type ===
                    INDEX_OBJECT_TYPES.PROPERTY &&
                    'border border-primary text-primary',
                )}
              >
                {INDEX_OBJECT_TYPES.PROPERTY}
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6 w-full">
          <Input value={currentInsuranceObject?.name} />
        </div>
        {INDEX_OBJECT_TYPES.VEHICLE ===
          currentInsuranceObject?.insurance_object_type && (
          <div className="mb-6 w-full">
            <Input value={currentAgreement?.registration_plate} />
          </div>
        )}
        <div className="mb-6 w-full">
          <Input value={currentIndex?.insurance_area} readOnly />
        </div>
        <div className="mb-6 w-full">
          <Input value={currentAgreement?.name} readOnly />
        </div>
        <div className="mb-6 w-full">
          <Input value={currentIndex?.name || ''} readOnly />
        </div>
        {showIgnoreDateErrorsCheckbox && (
          <label className="mt-6 flex cursor-pointer items-center justify-between gap-2 pl-5">
            <span className="text-sm text-gray-200">
              Ігнорувати перевірку файлів з помилками
            </span>
            <Checkbox.Root
              className="flex h-12 w-12 items-center justify-center rounded-md border border-gray-200"
              checked={ignoreDateErrors}
              onCheckedChange={(checked) =>
                setIgnoreDateErrors(checked === true)
              }
            >
              <Checkbox.Indicator>
                <IconClose className="h-4 w-4" />
              </Checkbox.Indicator>
            </Checkbox.Root>
          </label>
        )}
      </div>
      <div className="mt-5 flex flex-col gap-3">
        <div className="flex justify-end">
          <Button
            variant="destructive"
            onClick={handleSubmit}
            type="button"
            disabled={
              filesToUploadLength === 0 ||
              isUploading ||
              (hasInvalidFiles && !(ignoreDateErrors && hasOnlyDateErrors))
            }
          >
            Завантажити
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SecondStepFileAddFiles;
